import {
  Filter,
  type SelectItem,
  useSelectQueryGate,
} from '@vrplatform/components';
import { useLineMappingsQuery } from '@vrplatform/data';
import { formatPaymentLineType } from '@vrplatform/ui-common';
import type { Maybe } from '@vrplatform/utils';
import { useMemo } from 'react';
import type { BaseCustomFilterProps } from './_types';

interface Props extends BaseCustomFilterProps {
  lineType: Maybe<string>;
  setLineType: (value: string | null) => void;
}

export const ReservationLineTypeFilter = ({
  lineType,
  setLineType,
  label = 'Financial',
}: Props) => {
  const gate = useSelectQueryGate({ value: lineType });

  const queryData = useLineMappingsQuery({
    type: 'reservation',
    skip: !gate.shouldFetchAny,
  });

  const lineTypes = useMemo(() => {
    return (queryData.data ?? [])
      .map<SelectItem>((entry) => {
        const { line } = formatPaymentLineType(entry);

        return {
          value: entry.type,
          label: line,
        };
      })
      .sort((left, right) => left.label.localeCompare(right.label));
  }, [queryData.data]);

  const value = useMemo(() => {
    if (!lineType) return null;

    return lineTypes.find((entry) => entry.value === lineType) ?? null;
  }, [lineType, lineTypes]);

  return (
    <Filter.Select
      type="single"
      label={label}
      setValue={(next) => setLineType(next?.value ?? null)}
      value={value}
      data={{
        options: lineTypes,
        error: queryData.error,
        loading: queryData.isLoading,
      }}
      loadingMode="icon"
      onOpenChange={gate.onOpenChange}
    />
  );
};
